import { DownloadOutlined } from '@ant-design/icons'
import { Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Card, Form, Select, Switch, theme } from 'antd'
import * as React from 'react'
import ConditionalLocationSelector from '../ConditionalLocationSelector'
import type { LocationViewType } from './types'
import { LOCATION_VIEW_OPTIONS } from './constants'

interface LocationFiltersCardProps {
  allLocations: any[]
  isMetadataLoading: boolean
  originEnabled: boolean
  setOriginEnabled: (enabled: boolean) => void
  destinationEnabled: boolean
  setDestinationEnabled: (enabled: boolean) => void
  originGroupBy: LocationViewType
  setOriginGroupBy: (groupBy: LocationViewType) => void
  destinationGroupBy: LocationViewType
  setDestinationGroupBy: (groupBy: LocationViewType) => void
}

export function LocationFiltersCard({
  allLocations,
  isMetadataLoading,
  originEnabled,
  setOriginEnabled,
  destinationEnabled,
  setDestinationEnabled,
  originGroupBy,
  setOriginGroupBy,
  destinationGroupBy,
  setDestinationGroupBy,
}: LocationFiltersCardProps) {
  const { token } = theme.useToken()

  return (
    <Card size='small' title='Location Criteria'>
      <Vertical gap={12} flex='0' style={{ overflow: 'visible' }}>
        <Card
          size='small'
          title={
            <Horizontal verticalCenter gap={8} flex='0' style={{ overflow: 'visible' }}>
              <MaterialIcon icon='system_update_alt' style={{ fontSize: '16px', color: token.colorInfo }} />
              <Texto weight='bold' style={{ flex: 1 }}>
                Origin Filter
              </Texto>
              {originEnabled && (
                <Horizontal verticalCenter gap={8} style={{ marginRight: 16 }}>
                  <Texto category='label' appearance='hint'>
                    Group By:
                  </Texto>
                  <Select
                    size='small'
                    value={originGroupBy}
                    onChange={setOriginGroupBy}
                    options={LOCATION_VIEW_OPTIONS}
                    style={{ width: 120 }}
                  />
                </Horizontal>
              )}
            </Horizontal>
          }
          extra={<Switch checked={originEnabled} onChange={setOriginEnabled} />}
          style={{
            borderColor: originEnabled ? token.colorInfo : token.colorBorderSecondary,
            boxShadow: originEnabled ? `0 0 0 2px ${token.colorInfoBg}` : 'none',
            marginBottom: 0,
          }}
        >
          {originEnabled ? (
            <Vertical gap={8} flex='0' style={{ overflow: 'visible' }}>
              <Form.Item name='origin_terminals' style={{ marginBottom: 8 }}>
                <ConditionalLocationSelector
                  label='Origin Terminals'
                  placeholder='Select origin terminals'
                  locations={allLocations}
                  locationTypeFilter='terminals'
                  groupBy={originGroupBy}
                  loading={isMetadataLoading}
                />
              </Form.Item>
              <Form.Item name='origin_sites' style={{ marginBottom: 0 }}>
                <ConditionalLocationSelector
                  label='Origin Sites'
                  placeholder='Select origin sites'
                  locations={allLocations}
                  locationTypeFilter='sites'
                  groupBy={originGroupBy}
                  loading={isMetadataLoading}
                />
              </Form.Item>
            </Vertical>
          ) : (
            <Texto appearance='hint'>Enable to filter by origin locations</Texto>
          )}
        </Card>

        <Card
          size='small'
          title={
            <Horizontal verticalCenter gap={8} flex='0' style={{ overflow: 'visible' }}>
              <DownloadOutlined style={{ fontSize: '16px', color: token.colorSuccess }} />
              <Texto weight='bold' style={{ flex: 1 }}>
                Destination Filter
              </Texto>
              {destinationEnabled && (
                <Horizontal verticalCenter gap={8} style={{ marginRight: 16 }}>
                  <Texto category='label' appearance='hint'>
                    Group By:
                  </Texto>
                  <Select
                    size='small'
                    value={destinationGroupBy}
                    onChange={setDestinationGroupBy}
                    options={LOCATION_VIEW_OPTIONS}
                    style={{ width: 120 }}
                  />
                </Horizontal>
              )}
            </Horizontal>
          }
          extra={<Switch checked={destinationEnabled} onChange={setDestinationEnabled} />}
          style={{
            borderColor: destinationEnabled ? token.colorPrimaryBorder : token.colorBorderSecondary,
            boxShadow: destinationEnabled ? `0 0 0 2px ${token.colorPrimaryBgHover}` : 'none',
            marginBottom: 0,
          }}
        >
          {destinationEnabled ? (
            <Vertical gap={8} flex='0' style={{ overflow: 'visible' }}>
              <Form.Item name='destination_sites' style={{ marginBottom: 8 }}>
                <ConditionalLocationSelector
                  label='Destination Sites'
                  placeholder='Select destination sites'
                  locations={allLocations}
                  locationTypeFilter='sites'
                  groupBy={destinationGroupBy}
                  loading={isMetadataLoading}
                />
              </Form.Item>
              <Form.Item name='destination_terminals' style={{ marginBottom: 0 }}>
                <ConditionalLocationSelector
                  label='Destination Terminals'
                  placeholder='Select destination terminals'
                  locations={allLocations}
                  locationTypeFilter='terminals'
                  groupBy={destinationGroupBy}
                  loading={isMetadataLoading}
                />
              </Form.Item>
            </Vertical>
          ) : (
            <Texto appearance='hint'>Enable to filter by destination locations</Texto>
          )}
        </Card>
      </Vertical>
    </Card>
  )
}
